import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Collapse,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { useState } from 'react';
import {
  PiArrowSquareOut,
  PiArrowsLeftRight,
  PiCaretDown,
  PiCaretRight,
  PiDownloadSimple,
} from 'react-icons/pi';
import { Link } from 'wouter';
import {
  type InvoiceDetails,
  useDownloadInvoicePDF,
  useInvoiceDetailsByOfferId,
} from '@/models/invoices';
import { TOffer } from '@/models/offers';
import InvoiceDetail from './InvoiceDetail';
import { money } from './splitDerive';

type RowStatus = 'issued' | 'cancelled' | string;

type RelatedRow = {
  key: string;
  number: string;
  partLabel: string;
  status: RowStatus;
  current: boolean;
  amount: number;
  currency: string;
  /** Present once a real invoice backs the row, so its PDF can be downloaded. */
  invoiceId?: number;
};

const ROW_STATUS: Record<string, { color: string; label: string }> = {
  issued: { color: 'blue', label: 'Open' },
  cancelled: { color: 'gray', label: 'Cancelled' },
  paid: { color: 'teal', label: 'Settled' },
};

export default function RelatedInvoices({
  offer,
  canSplit,
  onSplit,
}: {
  offer: TOffer;
  canSplit: boolean;
  onSplit: () => void;
}) {
  const { data: invoices } = useInvoiceDetailsByOfferId(offer.id);
  const downloadPDF = useDownloadInvoicePDF();
  const [open, setOpen] = useState<string | null>(null);

  const initial = invoices?.find((i) => i.partNumber == null) ?? invoices?.[0];
  if (!initial) {
    return (
      <Paper withBorder p="md">
        <Text fw={600} mb="xs">
          Related invoices
        </Text>
        <Text fz="sm" c="dimmed">
          No invoice has been generated for this offer yet.
        </Text>
      </Paper>
    );
  }

  const rows: RelatedRow[] = [
    {
      key: 'initial',
      number: initial.invoiceNumber,
      partLabel: 'Initial',
      status: initial.status,
      current: true,
      amount: Number(initial.dueAmount) || 0,
      currency: initial.dueCurrency,
      invoiceId: initial.id,
    },
  ];

  return (
    <Paper withBorder p="md">
      <Group justify="space-between" align="center" mb="md">
        <Text fw={600} fz="lg">
          Related invoices
        </Text>
        {canSplit && (
          <Button
            size="xs"
            color="green"
            leftSection={<PiArrowsLeftRight size={14} />}
            onClick={onSplit}
          >
            Split payment
          </Button>
        )}
      </Group>

      <Stack gap={6}>
        {rows.map((row) => {
          const meta = ROW_STATUS[row.status] ?? {
            color: 'gray',
            label: row.status,
          };
          const expanded = open === row.key;
          return (
            <Box
              key={row.key}
              style={{
                borderRadius: 6,
                border: row.current
                  ? '1px solid var(--mantine-color-blue-5)'
                  : '1px solid var(--mantine-color-default-border)',
              }}
            >
              <Group
                justify="space-between"
                wrap="nowrap"
                gap="md"
                px="sm"
                py="xs"
              >
                <UnstyledButton
                  onClick={() => setOpen(expanded ? null : row.key)}
                  style={{ flex: 1, minWidth: 0 }}
                >
                  <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                    {expanded ? (
                      <PiCaretDown size={14} />
                    ) : (
                      <PiCaretRight size={14} />
                    )}
                    <Text
                      fw={500}
                      fz="sm"
                      td={
                        row.status === 'cancelled' ? 'line-through' : undefined
                      }
                      c={row.number === '—' ? 'dimmed' : undefined}
                    >
                      {row.number}
                    </Text>
                    <Badge size="sm" variant="default">
                      {row.partLabel}
                    </Badge>
                    {row.current && (
                      <Badge size="sm" color="blue" variant="light">
                        Current
                      </Badge>
                    )}
                  </Group>
                </UnstyledButton>
                <Group gap="md" wrap="nowrap">
                  <Badge color={meta.color} variant="light">
                    {meta.label}
                  </Badge>
                  <Text fw={600} fz="sm" style={{ whiteSpace: 'nowrap' }}>
                    {money(row.amount, row.currency)}
                  </Text>
                  {row.invoiceId != null && (
                    // The number itself sits inside the expand button, so the way
                    // through to the invoice is its own control rather than a link
                    // nested in a button.
                    <Tooltip label="Open invoice">
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        component={Link}
                        to={`~/invoices/${row.invoiceId}`}
                      >
                        <PiArrowSquareOut size={14} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  {row.invoiceId != null && (
                    <Tooltip label="Download invoice">
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        loading={
                          downloadPDF.isPending &&
                          downloadPDF.variables === String(row.invoiceId)
                        }
                        onClick={() =>
                          downloadPDF.mutate(String(row.invoiceId))
                        }
                      >
                        <PiDownloadSimple size={14} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              </Group>
              <Collapse expanded={expanded}>
                <Box px="sm" pb="sm">
                  <InvoiceDetail
                    invoice={initial as InvoiceDetails}
                    offer={offer}
                  />
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}
